import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect, useDispatch } from "react-redux";
import { injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import { login, socialLogin, getUserOrganizations, initiateSso } from "../_redux/authCrud";
import { consumePendingInvite } from "../pendingInvite";
import axios from "axios";
import appConfig from "../../../../config";
import './auth.scss';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider, useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../../../auth/microsoftConfig';

const initialValues = {
  email: "",
  password: "", 
  remember: false,
};

function Login(props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [ssoMode, setSsoMode] = useState(false);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState('');
  const dispatch = useDispatch();
  const location = useLocation();
  const { instance } = useMsal();


  const from = location.state?.from?.pathname || "/";

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(3, "Password must be at least 3 characters")
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setStatus('');
      
      try {
        const response = await login({ email: values.email, password: values.password });
        const { user, tokens } = response.data;
        await handlePostAuth(user, tokens);
      } catch (error) {
        const message = error.response?.data?.message || 'Login failed';
        setStatus(message);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handlePostAuth = async (user, tokens) => {
    if (tokens && tokens.access && tokens.refresh) {
      props.login(tokens.access.token, tokens.refresh.token);
      dispatch(auth.actions.fulfillUser(user));

      if (user && !user.email_verified) {
        navigate('/auth/activation', { state: { email: user.email } });
        return;
      }

      // If this login was triggered from an invite link, join that org now
      // that we have a valid auth token (real accept-invite requires one).
      await consumePendingInvite(axios, appConfig.URL);

      try {
        const orgsResponse = await getUserOrganizations();
        const organizations = orgsResponse.data.organizations || [];
        dispatch(auth.actions.fulfillOrgs(organizations));
        
        if (organizations.length === 0) {
          navigate('/auth/organization-setup', { 
            state: { user, fromRegistration: false } 
          });
        } else {
          // ✅ SUCCESS: Redirect back to the original URL
          navigate(from, { replace: true });
        }
      } catch (error) {
        dispatch(auth.actions.fulfillOrgs([]));
        navigate('/auth/organization-setup', { 
          state: { user, fromRegistration: false } 
        });
      }
    }
  };

  // ✅ One-Tap Auto Login
  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      setSocialLoading('google');
      formik.setStatus('');
      
      try {
        // One-Tap gives JWT credential - backend handles this as ID token
        const response = await socialLogin({
          provider: 'google',
          token: credentialResponse.credential,
        });
        
        const { user, tokens } = response.data;
        await handlePostAuth(user, tokens);
      } catch (error) {
        console.error('One-tap error:', error);
        const message = error.response?.data?.message || 'Google sign-in failed';
        formik.setStatus(message);
      } finally {
        setSocialLoading(null);
      }
    },
    onError: () => console.log('One-tap failed'),
    auto_select: true,
    cancel_on_tap_outside: false,
  });

  // ✅ Custom Google Button
  const handleGoogleSignIn = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setSocialLoading('google');
      formik.setStatus('');
      
      try {
        // Get user info
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const userInfo = await userInfoResponse.json();
        
        // Send access_token + email + name to backend
        const response = await socialLogin({
          provider: 'google',
          token: tokenResponse.access_token,
          email: userInfo.email,
          name: userInfo.name,
        }); 
        
        const { user, tokens } = response.data;
        await handlePostAuth(user, tokens);
        
      } catch (error) {
        console.error('Google sign-in error:', error);
        const message = error.response?.data?.message || 'Google sign-in failed';
        formik.setStatus(message);
      } finally {
        setSocialLoading(null);
      }
    },
    onError: () => {
      console.error('Google sign-in failed');
      formik.setStatus('Google sign-in was cancelled or failed');
    },
  });

  const handleMicrosoftSignIn = async () => {
    setSocialLoading('microsoft');
    formik.setStatus('');
    try {
      const result = await instance.loginPopup(loginRequest);
      const response = await socialLogin({
        provider: 'microsoft',
        token: result.accessToken,
        email: result.account?.username,
        name: result.account?.name,
      });
      const { user, tokens } = response.data;
      await handlePostAuth(user, tokens);
    } catch (error) {
      if (error?.errorCode !== 'user_cancelled') {
        const message = error.response?.data?.message || 'Microsoft sign-in failed';
        formik.setStatus(message);
      }
    } finally {
      setSocialLoading(null);
    }
  };

  const handleSsoSubmit = async () => {
    if (!ssoEmail.trim()) { setSsoError('Please enter your work email.'); return; }
    setSsoLoading(true);
    setSsoError('');
    try {
      const res = await initiateSso(ssoEmail.trim());
      const url = res.data?.data?.url || res.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setSsoError('SSO is not configured for this email domain.');
      }
    } catch (err) {
      setSsoError(err.response?.data?.message || 'SSO is not available for this organization.');
    } finally {
      setSsoLoading(false);
    }
  };

  // ✅ Apple Sign-In
  const handleAppleSuccess = async (response) => {
    setSocialLoading('apple');
    formik.setStatus('');
    
    try {
      const appleResponse = await socialLogin({
        provider: 'apple',
        token: response.authorization.id_token,
      });
      
      const { user, tokens } = appleResponse.data;
      await handlePostAuth(user, tokens);
      
    } catch (error) {
      console.error('Apple sign-in error:', error);
      const message = error.response?.data?.message || 'Apple sign-in failed';
      formik.setStatus(message);
    } finally {
      setSocialLoading(null);
    }
  };

  if (ssoMode) {
    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <div className="clean-login-card">
          <div className="login-header">
            <h1 className="login-title">Sign in with SSO</h1>
            <p className="login-subtitle">Enter your work email to continue via your organization's identity provider.</p>
          </div>

          {ssoError && <div className="login-alert">{ssoError}</div>}

          <div className="login-form">
            <div className="form-group">
              <label className="form-label">Work email</label>
              <input
                type="email"
                className="form-control"
                placeholder="you@company.com"
                value={ssoEmail}
                onChange={e => setSsoEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSsoSubmit()}
                autoFocus
              />
            </div>

            <button
              type="button"
              className="submit-btn"
              onClick={handleSsoSubmit}
              disabled={ssoLoading}
            >
              {ssoLoading ? <>Checking…<span className="spinner" /></> : 'Continue with SSO'}
            </button>

            <div className="form-footer">
              <button type="button" className="footer-link" onClick={() => { setSsoMode(false); setSsoError(''); }}>
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </GoogleOAuthProvider>
    );
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <div className="clean-login-card">
        <div className="login-header">
          <h1 className="login-title">Sign in</h1>
          <p className="login-subtitle">Welcome back! Please enter your details.</p>
        </div>

        {formik.status && (
          <div className="login-alert">
            {formik.status}
          </div>
        )}

        {/* Social Auth */}
        <div className="social-auth">
          {/* Microsoft */}
          <button
            type="button"
            onClick={handleMicrosoftSignIn}
            disabled={socialLoading !== null}
            className="google-signin-btn"
          >
            {socialLoading === 'microsoft' ? (
              <span className="spinner"></span>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="1" y="1" width="8.5" height="8.5" fill="#F25022"/>
                  <rect x="10.5" y="1" width="8.5" height="8.5" fill="#7FBA00"/>
                  <rect x="1" y="10.5" width="8.5" height="8.5" fill="#00A4EF"/>
                  <rect x="10.5" y="10.5" width="8.5" height="8.5" fill="#FFB900"/>
                </svg>
                Continue with Microsoft
              </>
            )}
          </button>

          {/* Google */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={socialLoading !== null}
            className="google-signin-btn"
          >
            {socialLoading === 'google' ? (
              <span className="spinner"></span>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M19.8055 10.2292C19.8055 9.55135 19.7508 8.86719 19.6344 8.19661H10.2002V12.0492H15.6014C15.3773 13.2911 14.6571 14.3898 13.6025 15.0879V17.5866H16.825C18.7171 15.8449 19.8055 13.2728 19.8055 10.2292Z" fill="#4285F4"/>
                  <path d="M10.2002 20.0006C12.9515 20.0006 15.2664 19.1151 16.8251 17.5865L13.6026 15.0879C12.7015 15.6979 11.5469 16.0433 10.2002 16.0433C7.54257 16.0433 5.29429 14.2834 4.50543 11.9097H1.17773V14.4819C2.7748 17.6544 6.31114 20.0006 10.2002 20.0006Z" fill="#34A853"/>
                  <path d="M4.50543 11.9097C4.04543 10.6678 4.04543 9.33337 4.50543 8.09148V5.51929H1.17773C-0.290952 8.43984 -0.290952 11.5613 1.17773 14.4819L4.50543 11.9097Z" fill="#FBBC04"/>
                  <path d="M10.2002 3.95805C11.6244 3.936 13.0009 4.47508 14.0401 5.45894L16.8935 2.60552C15.1809 0.990833 12.9297 0.0869522 10.2002 0.109818C6.31114 0.109818 2.7748 2.45601 1.17773 5.51931L4.50543 8.0915C5.29429 5.71783 7.54257 3.95805 10.2002 3.95805Z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          {/* Apple Sign-In */}
          {/* <AppleSignin
            authOptions={{
              clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
              scope: 'email name',
              redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI,
              state: 'state',
              nonce: 'nonce',
              usePopup: true,
            }}
            onSuccess={handleAppleSuccess}
            onError={(error) => {
              console.error('Apple error:', error);
              formik.setStatus('Apple sign-in was cancelled or failed');
            }}
            render={(props) => (
              <button
                {...props}
                type="button"
                className="apple-signin-btn"
                disabled={socialLoading !== null}
              >
                {socialLoading === 'apple' ? (
                  <span className="spinner"></span>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M15.5 10.5c0-2.5 2-3.5 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.6.9-.8 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.5 3 2.4 1.2 0 1.7-.8 3.1-.8 1.5 0 1.9.8 3.1.8 1.3 0 2.1-1.1 2.9-2.3.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.4-1-2.6-3.9z"/>
                      <path d="M13 3.5c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1.1.1 2.1-.6 2.7-1.3z"/>
                    </svg>
                    Continue with Apple
                  </>
                )}
              </button>
            )}
          /> */}

          <button
            type="button"
            onClick={() => { setSsoMode(true); setSsoEmail(formik.values.email || ''); setSsoError(''); }}
            disabled={socialLoading !== null}
            className="google-signin-btn"
            style={{ marginTop: 8 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#5341C4' }}>
              <rect x="5" y="2" width="14" height="20" rx="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
            Sign in with SSO
          </button>

          <div className="divider">
            <span>Or continue with email</span>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className="login-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              className={`form-control ${formik.touched.email && formik.errors.email ? 'error' : ''}`}
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="form-error">{formik.errors.email}</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                className={`form-control ${formik.touched.password && formik.errors.password ? 'error' : ''}`}
                {...formik.getFieldProps("password")}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className="form-error">{formik.errors.password}</div>
            )}
          </div>

          <div className="form-options">
            <label className="remember-check">
              <input type="checkbox" name="remember" {...formik.getFieldProps("remember")} />
              <span>Remember me</span>
            </label>
            <button type="button" className="forgot-link" onClick={() => navigate('/auth/forgot-password')}>
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={formik.isSubmitting || loading}
            className="submit-btn"
          >
            {loading ? (
              <>
                Signing in...
                <span className="spinner"></span>
              </>
            ) : (
              'Sign in'
            )}
          </button>

          <div className="form-footer">
            Don't have an account?{' '}
            <button type="button" className="footer-link" onClick={() => navigate('/auth/registration')}>
              Sign up
            </button>
          </div>
        </form>
      </div>
    </GoogleOAuthProvider>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));